import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { BaseApiService } from './base-api.service';
import {
  WorkSheet,
  WorkSheetActionResult,
  WorkSheetEntry,
  WorkSheetEntryFilters,
  PaginatedResponse,
  ApiResponse,
  WorkSheetsSummary,
} from '@com.maessa.payroll.frontend/models';

export interface WorkSheetFilters {
  date?: string;
  date_from?: string;
  date_to?: string;
  status?: string | string[];
  origin?: 'legacy_import' | 'app' | string;
  shift?: string | string[];
  has_open_incidents?: number | boolean;
  employee?: string | number;
  oott_id?: string | number | (string | number)[];
  project_id?: string | number | (string | number)[];
  activity_id?: string | number | (string | number)[];
  site_id?: number;
  sort?: string;
  page?: number;
  per_page?: number;
  limit?: number;
  /** @deprecated Removido en MAEP-19 para el explorador de partes */
  team_id?: number;
}

export interface WorkSheetPayload {
  date: string;
  shift: string;
  team_id: number;
}

@Injectable({ providedIn: 'root' })
export class WorkSheetApiService extends BaseApiService {
  getWorkSheets(filters?: WorkSheetFilters): Observable<PaginatedResponse<WorkSheet>> {
    const rawFilters: Record<string, unknown> = { ...(filters ?? {}) };
    if (rawFilters['per_page'] !== undefined && rawFilters['limit'] === undefined) {
      rawFilters['limit'] = rawFilters['per_page'];
      delete rawFilters['per_page'];
    }

    if (!rawFilters['sort']) {
      rawFilters['sort'] = '-workDate';
    }

    for (const [key, value] of Object.entries(rawFilters)) {
      if (Array.isArray(value)) {
        rawFilters[key] = value.join(',');
      } else if (typeof value === 'boolean') {
        rawFilters[key] = value ? 1 : 0;
      }
    }

    const params = this.buildParams(rawFilters);
    return this.get<PaginatedResponse<WorkSheet>>(`${this.baseUrl}/worksheets`, params);
  }

  getWorkSheetsSummary(filters?: WorkSheetFilters): Observable<WorkSheetsSummary> {
    const rawFilters: Record<string, unknown> = { ...(filters ?? {}) };
    delete rawFilters['page'];
    delete rawFilters['limit'];
    delete rawFilters['per_page'];
    delete rawFilters['sort'];

    for (const [key, value] of Object.entries(rawFilters)) {
      if (Array.isArray(value)) {
        rawFilters[key] = value.join(',');
      } else if (typeof value === 'boolean') {
        rawFilters[key] = value ? 1 : 0;
      }
    }

    const params = this.buildParams(rawFilters);
    return this.get<WorkSheetsSummary>(`${this.baseUrl}/worksheets/summary`, params);
  }

  getWorkSheet(id: number): Observable<WorkSheet> {
    return this.get<WorkSheet>(`${this.baseUrl}/worksheets/${id}`);
  }

  /** GET /api/v1/worksheets/{id}/entries — entradas paginadas y filtrables */
  getWorkSheetEntries(
    id: number,
    filters?: WorkSheetEntryFilters
  ): Observable<PaginatedResponse<WorkSheetEntry>> {
    const raw: Record<string, unknown> = { ...(filters ?? {}) };
    if (typeof raw['has_incidents'] === 'boolean') {
      raw['has_incidents'] = raw['has_incidents'] ? 1 : 0;
    }
    const params = this.buildParams(raw);
    return this.get<PaginatedResponse<WorkSheetEntry>>(
      `${this.baseUrl}/worksheets/${id}/entries`,
      params
    );
  }

  getTodaySheet(teamId: number): Observable<ApiResponse<WorkSheet | null>> {
    const params = this.buildParams({ team_id: teamId, today: true });
    return this.get<ApiResponse<WorkSheet | null>>(`${this.baseUrl}/worksheets/today`, params);
  }

  createWorkSheet(payload: WorkSheetPayload): Observable<ApiResponse<WorkSheet>> {
    return this.post<ApiResponse<WorkSheet>>(`${this.baseUrl}/worksheets`, payload);
  }

  submit(id: number, notes?: string): Observable<WorkSheetActionResult> {
    return this.post<WorkSheetActionResult>(
      `${this.baseUrl}/worksheets/${id}/submit`,
      { notes }
    );
  }

  validate(id: number): Observable<WorkSheetActionResult> {
    return this.post<WorkSheetActionResult>(
      `${this.baseUrl}/worksheets/${id}/validate`,
      {}
    );
  }

  approve(id: number, notes?: string): Observable<WorkSheetActionResult> {
    return this.post<WorkSheetActionResult>(
      `${this.baseUrl}/worksheets/${id}/approve`,
      { notes }
    );
  }

  reject(id: number, reason: string): Observable<WorkSheetActionResult> {
    return this.post<WorkSheetActionResult>(
      `${this.baseUrl}/worksheets/${id}/reject`,
      { reason }
    );
  }

  consolidate(id: number): Observable<WorkSheetActionResult> {
    return this.post<WorkSheetActionResult>(
      `${this.baseUrl}/worksheets/${id}/consolidate`,
      {}
    );
  }
}
